import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Play, Square, ChevronDown } from 'lucide-react';
import { Project, ActiveTimer } from '../../types/time';

const MAX_VISIBLE = 5;

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function ProjectButton({ project, isActive, loading, onClick }: {
  project: Project;
  isActive: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
        whitespace-nowrap transition-all duration-200 border
        ${isActive
          ? 'border-current shadow-sm ring-1 ring-current'
          : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
        }
        ${loading ? 'opacity-60 cursor-wait' : 'cursor-pointer'}
      `}
      style={isActive ? { color: project.color, borderColor: project.color } : {}}
    >
      {isActive ? (
        <span className="relative flex h-2 w-2">
          <span
            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            style={{ backgroundColor: project.color }}
          />
          <span
            className="relative inline-flex rounded-full h-2 w-2"
            style={{ backgroundColor: project.color }}
          />
        </span>
      ) : (
        <span
          className="inline-flex rounded-full h-2 w-2 flex-shrink-0"
          style={{ backgroundColor: project.color }}
        />
      )}
      {project.name}
      {isActive ? (
        <Square size={12} className="ml-1 flex-shrink-0" />
      ) : (
        <Play size={12} className="ml-1 flex-shrink-0 text-gray-400" />
      )}
    </button>
  );
}

const ProjectButtonsBar: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, activeRes] = await Promise.all([
        axios.get('/api/projects?status=active'),
        axios.get('/api/time-entries/active')
      ]);
      setProjects(projectsRes.data);
      setActiveTimer(activeRes.data);
    } catch (err) {
      console.error('Error fetching project bar data:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const onChanged = () => fetchData();
    window.addEventListener('time-entries-changed', onChanged);
    return () => window.removeEventListener('time-entries-changed', onChanged);
  }, [fetchData]);

  useEffect(() => {
    if (activeTimer) {
      const startTime = new Date(activeTimer.start_time).getTime();
      const updateElapsed = () => {
        setElapsed(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
      };
      updateElapsed();
      intervalRef.current = setInterval(updateElapsed, 1000);
    } else {
      setElapsed(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [activeTimer]);

  // Close "more" dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    if (moreOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreOpen]);

  const handleProjectClick = async (projectId: number) => {
    if (loading) return;
    setLoading(true);
    setMoreOpen(false);

    const previousTimer = activeTimer;

    try {
      setError(null);
      if (activeTimer && activeTimer.project_id === projectId) {
        setActiveTimer(null);
        await axios.patch(`/api/time-entries/${previousTimer!.id}/stop`);
      } else {
        const project = projects.find(p => p.id === projectId);
        setActiveTimer({
          id: -1,
          project_id: projectId,
          project_name: project?.name || '',
          project_color: project?.color || '#3B82F6',
          start_time: new Date().toISOString(),
          description: null
        });
        const res = await axios.post('/api/time-entries/start', { project_id: projectId });
        setActiveTimer(prev => prev ? { ...res.data, start_time: prev.start_time } : res.data);
      }
      window.dispatchEvent(new Event('time-entries-changed'));
    } catch (err: any) {
      console.error('Error toggling timer:', err);
      setActiveTimer(previousTimer);
      const msg = err.response?.data?.error || 'Failed to toggle timer';
      setError(msg);
      setTimeout(() => setError(null), 4000);
    } finally {
      setLoading(false);
    }
  };

  if (projects.length === 0) return null;

  // If the active timer is on a project outside the top 5, promote it into the visible set
  const activeProjectId = activeTimer?.project_id;
  let visibleProjects: Project[];
  let overflowProjects: Project[];

  if (projects.length <= MAX_VISIBLE) {
    visibleProjects = projects;
    overflowProjects = [];
  } else {
    const top = projects.slice(0, MAX_VISIBLE);
    const rest = projects.slice(MAX_VISIBLE);
    const activeInRest = activeProjectId ? rest.find(p => p.id === activeProjectId) : null;

    if (activeInRest) {
      visibleProjects = [activeInRest, ...top.slice(0, MAX_VISIBLE - 1)];
      overflowProjects = projects.filter(p => !visibleProjects.some(v => v.id === p.id));
    } else {
      visibleProjects = top;
      overflowProjects = rest;
    }
  }

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 py-2">
          {visibleProjects.map((project) => (
            <ProjectButton
              key={project.id}
              project={project}
              isActive={activeProjectId === project.id}
              loading={loading}
              onClick={() => handleProjectClick(project.id)}
            />
          ))}

          {overflowProjects.length > 0 && (
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium
                  border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                +{overflowProjects.length} more
                <ChevronDown size={12} />
              </button>

              {moreOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
                  {overflowProjects.map((project) => {
                    const isActive = activeProjectId === project.id;
                    return (
                      <button
                        key={project.id}
                        onClick={() => handleProjectClick(project.id)}
                        disabled={loading}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-left hover:bg-gray-50 ${
                          isActive ? 'bg-gray-50' : ''
                        }`}
                      >
                        <span
                          className={`inline-flex rounded-full h-2 w-2 flex-shrink-0 ${isActive ? 'animate-pulse' : ''}`}
                          style={{ backgroundColor: project.color }}
                        />
                        <span className="flex-1 truncate">{project.name}</span>
                        {isActive ? (
                          <Square size={12} className="flex-shrink-0" style={{ color: project.color }} />
                        ) : (
                          <Play size={12} className="flex-shrink-0 text-gray-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {error && (
            <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded ml-2">{error}</span>
          )}

          {activeTimer && (
            <div className="ml-auto flex items-center gap-2 pl-4 flex-shrink-0">
              <span className="text-sm font-mono font-semibold text-gray-900">
                {formatElapsed(elapsed)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectButtonsBar;
